import { useMemo, useCallback } from 'react';

/**
 * useKanbanViewProps Hook
 * 
 * Assembles all props needed for the KanbanView component.
 */
const useKanbanViewProps = ({
  tasks,
  statuses,
  localSprint,
  allSprints = [],
  setLocalSprint,
  calculatedProgress,
  editingKanbanColumnId,
  setEditingKanbanColumnId,
  setDetailSidebarTaskId,
  setAttachmentsModalTaskId,
  cardDisplaySettingsPerView,
  swimlaneSetting,
  sortSettingsPerView,
  filtersPerView,
  showArchived,
  showActionableOnly,
  isTemplate = false,
  actions,
  handlers,
  modals,
  nested,
  handleTrashNestedSprint,
  onSprintIconChange,
  // Handlers from parent
  handleDeleteTaskWithNestedCheck,
  handleDeleteTasksWithNestedCheck,
  handleIconChangeWithNestedSync,
  // View navigation (for "Open in view" button)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  // Deliverables warning modal
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  onSelectSprint,
}) => {
  // Handle column reorder for Kanban
  const handleColumnReorder = useCallback(async (newOrder) => {
    console.log('📋 Column reorder:', newOrder);
    const updatedSprint = { ...localSprint, kanbanColumnOrder: newOrder };
    setLocalSprint(updatedSprint);
    try {
      const { SprintAPI } = await import('../../../../utils/api');
      await SprintAPI.update(localSprint.id, updatedSprint);
      console.log('✅ Column order saved to API');
    } catch (error) {
      console.error('Failed to save column order:', error);
    }
  }, [localSprint, setLocalSprint]);

  // Handle reassigning task statuses (batch)
  const handleReassignTasksStatus = useCallback((taskIds, newStatusId) => {
    taskIds.forEach(taskId => {
      actions.handleTaskStatusChange(taskId, newStatusId);
    });
  }, [actions]);

  // Handle "go to task in view" - dispatches focusMission event
  const handleGoToTaskInView = useCallback((taskId, viewType) => {
    // Dispatch event for the target view to handle focusing
    window.dispatchEvent(new CustomEvent('focusMission', {
      detail: { missionId: taskId, view: viewType }
    }));
  }, []);

  return useMemo(() => ({
    tasks,
    statuses,
    categories: actions.getAllCategories(),
    allTags: actions.getAllTags(),
    nestedSprints: nested.nestedSprints,
    localSprint,
    allSprints,
    campaignId: localSprint?.id || '',
    campaignName: localSprint?.name,
    campaignIcon: localSprint?.icon,
    campaignProgress: calculatedProgress,
    breadcrumbPath: nested.breadcrumbPath,
    onNavigateToParent: nested.handleGoToParent,
    onSelectSprint,
    onSprintIconChange,
    onRenameSprint: handlers.handleRenameSprint,
    kanbanColumnOrder: localSprint?.kanbanColumnOrder || [],
    editingColumnId: editingKanbanColumnId,
    onStatusChange: modals.handleStatusChangeWithModal,
    onCategoryChange: actions.handleCategoryChange,
    onRenameCategory: actions.handleRenameCategory,
    onDeleteCategory: actions.handleDeleteCategory,
    onAddTag: actions.handleAddTag,
    onRemoveTag: actions.handleRemoveTag,
    onSetPrimaryTag: actions.handleSetPrimaryTag,
    onRenameTag: actions.handleRenameTag,
    onDeleteTag: actions.handleDeleteTag,
    onOpenNestedSprint: nested.handleOpenNestedSprint,
    onCreateNestedSprint: nested.handleCreateNestedSprint,
    onRemoveNestedSprint: handleTrashNestedSprint,
    onPasteTasks: handlers.handlePasteTasks,
    onDeleteTask: handleDeleteTaskWithNestedCheck,
    onDeleteTasks: handleDeleteTasksWithNestedCheck,
    onDuplicateTask: actions.handleDuplicateTask,
    onCreateParallelMission: handlers.handleCreateParallelMission,
    onInsertMissionBefore: handlers.handleInsertMissionBefore,
    onColumnReorder: handleColumnReorder,
    onAddTask: (statusId, swimlaneId) => {
      const options = {};
      if (swimlaneSetting === 'tag' && swimlaneId && swimlaneId !== '__empty__') {
        options.tags = [swimlaneId];
      }
      return actions.handleAddTask(statusId, null, 'task', options);
    },
    onAddDecision: (statusId, swimlaneId) => {
      const options = {};
      if (swimlaneSetting === 'tag' && swimlaneId && swimlaneId !== '__empty__') {
        options.tags = [swimlaneId];
      }
      return actions.handleAddTask(statusId, null, 'decision', options);
    },
    onAddWait: (statusId, swimlaneId) => {
      const options = {};
      if (swimlaneSetting === 'tag' && swimlaneId && swimlaneId !== '__empty__') {
        options.tags = [swimlaneId];
      }
      return actions.handleAddTask(statusId, null, 'wait', options);
    },
    onIconChange: handleIconChangeWithNestedSync,
    onRename: actions.handleRenameTask,
    onRenameStatus: handlers.handleRenameStatus,
    onDeleteStatus: handlers.handleDeleteStatus,
    onAddStatus: handlers.handleAddStatus,
    onReassignTasksStatus: handleReassignTasksStatus,
    onSetWipLimit: handlers.handleSetWipLimit,
    onFinishColumnEdit: () => setEditingKanbanColumnId(null),
    onOpenDetails: setDetailSidebarTaskId,
    onOpenAttachments: setAttachmentsModalTaskId,
    onDueDateChange: actions.handleDueDateChange,
    onRemindersChange: actions.handleRemindersChange,
    onEstimatedDurationMinutesChange: actions.handleEstimatedDurationMinutesChange,
    onCreatedAtChange: actions.handleCreatedAtChange,
    onStartedAtChange: actions.handleStartedAtChange,
    onCompletedAtChange: actions.handleCompletedAtChange,
    onUpdatedAtChange: actions.handleUpdatedAtChange,
    onPriorityChange: actions.handlePriorityChange,
    onDifficultyChange: actions.handleDifficultyChange,
    onCustomFieldValueChange: actions.handleCustomFieldValueChange,
    customFields: localSprint?.customFields || [],
    onSetStatusAutoSync: actions.handleSetStatusAutoSync,
    onDeliverablesChange: actions.handleDeliverablesChange,
    onInputsChange: actions.handleInputsChange,
    onArchiveTask: actions.handleArchiveTask,
    onUnarchiveTask: actions.handleUnarchiveTask,
    onDependencyChange: actions.handleDependencyChange,
    onDecisionAnswerChange: actions.handleDecisionAnswerChange,
    onDecisionOptionsChange: actions.handleDecisionOptionsChange,
    cardDisplaySettings: cardDisplaySettingsPerView?.kanban,
    swimlaneSetting,
    sortSetting: sortSettingsPerView?.kanban || 'manual',
    filters: filtersPerView?.kanban || {},
    showArchived,
    showActionableOnly,
    isTemplate,
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    onGoToTaskInView: handleGoToTaskInView,
    onSubmitDeliverable,
    onEditDeliverables,
    onClearDeliverable,
  }), [
    tasks, statuses, actions, nested, localSprint, allSprints, calculatedProgress,
    editingKanbanColumnId, modals, handlers, handleTrashNestedSprint,
    handleColumnReorder, handleReassignTasksStatus, setEditingKanbanColumnId,
    setDetailSidebarTaskId, setAttachmentsModalTaskId, cardDisplaySettingsPerView, swimlaneSetting, sortSettingsPerView,
    filtersPerView, showArchived, showActionableOnly, isTemplate, onSprintIconChange,
    handleIconChangeWithNestedSync, handleDeleteTaskWithNestedCheck, handleDeleteTasksWithNestedCheck,
    currentView, customViews, activeCustomViewId, onViewChange, onSelectCustomView, handleGoToTaskInView,
    onSubmitDeliverable, onEditDeliverables, onClearDeliverable, onSelectSprint,
  ]);
};

export default useKanbanViewProps;
